/**
 * AddressesTab — every delivery address this customer has used, with a per-
 * channel breakdown (Shopee / Lazada / TikTok / Storefront). Rendered in the
 * Customer detail ExperienceView when the "ที่อยู่" tab is active.
 *
 * Follows the detail-page section pattern: a SectionHeader above ONE card whose
 * addresses are divider-separated rows — no box-within-a-box. Data comes from
 * `useCustomerAddresses` (linked Address records + order shipping usage).
 */

import { Fragment } from "react";
import { MapPin, Star, Phone, Package } from "lucide-react";
import { CARD } from "@/config/design-tokens";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useCustomerAddresses, type CustomerAddress } from "@/features/customers/api/customerHooks";
import { ChannelLogo } from "@/features/channels/components/ChannelLogo";

function isMarketplace(channel: string): boolean {
  return /shopee|lazada|tiktok/i.test(channel);
}

/** One channel-usage chip: brand logo (marketplace) + count. */
function ChannelChip({ channel, count }: { channel: string; count: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1 text-[12px] text-muted-foreground">
      {isMarketplace(channel) && <ChannelLogo channel={channel} size={14} className="rounded" />}
      <span className="font-medium text-foreground/80">{channel}</span>
      <span className="opacity-60">· {count}</span>
    </span>
  );
}

/** One address, as a padded row inside the shared card (no border of its own). */
function AddressRow({ addr }: { addr: CustomerAddress }) {
  const region = [addr.city, addr.state, addr.pincode].filter(Boolean).join(" ");
  const channels = Object.entries(addr.channels).sort((a, b) => b[1] - a[1]);

  return (
    <div className="px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 space-y-0.5 text-[14px] leading-snug text-foreground/90">
            {addr.line1 && <div>{addr.line1}</div>}
            {addr.line2 && <div>{addr.line2}</div>}
            {region && <div className="text-muted-foreground">{region}</div>}
            {addr.country && addr.country !== "Thailand" && (
              <div className="text-muted-foreground">{addr.country}</div>
            )}
            {addr.phone && (
              <div className="flex items-center gap-1.5 pt-0.5 text-[13px] text-muted-foreground">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                {addr.phone}
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {addr.isPrimary && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              <Star className="h-3 w-3" />
              หลัก
            </span>
          )}
          {addr.addressType && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {addr.addressType === "Shipping" ? "จัดส่ง" : addr.addressType === "Billing" ? "เรียกเก็บ" : addr.addressType}
            </span>
          )}
        </div>
      </div>

      {(addr.orderCount > 0 || channels.length > 0) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 pl-[26px]">
          <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <Package className="h-3.5 w-3.5 shrink-0" />
            ใช้กับ {addr.orderCount} ออเดอร์
          </span>
          {channels.map(([ch, n]) => (
            <ChannelChip key={ch} channel={ch} count={n} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AddressesTab({ customer }: { customer: string }) {
  const { data: addresses, isLoading } = useCustomerAddresses(customer);
  const list = addresses ?? [];

  return (
    <div className="px-4 py-4 md:px-6">
      <div className="flex items-center justify-between">
        <SectionHeader label="ที่อยู่ที่เคยใช้" />
        {list.length > 0 && (
          <span className="text-[13px] text-muted-foreground">{list.length} ที่อยู่</span>
        )}
      </div>

      {isLoading ? (
        <div className={CARD}>
          <div className="px-5 py-4 text-[14px] text-muted-foreground">กำลังโหลด…</div>
        </div>
      ) : list.length === 0 ? (
        <div className={CARD}>
          <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
            <MapPin className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-[14px] text-muted-foreground">ยังไม่มีที่อยู่สำหรับลูกค้ารายนี้</p>
          </div>
        </div>
      ) : (
        <div className={CARD}>
          {list.map((addr, i) => (
            <Fragment key={addr.name}>
              {i > 0 && <div className="mx-5 border-t border-border/40" />}
              <AddressRow addr={addr} />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
